/** @jsx jsx */
import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  Heading,
  jsx,
  Text,
} from 'theme-ui'
import VisuallyHidden from '~/components/Generic/VisuallyHidden'
import StarIcon from '~/assets/images/icons/star.inline.svg'

const ReviewListSectionFeaturedReview = ({ isActive, review }) => {
  const { author, authorDescription, body, starCount, title } = review

  return (
    <Box
      sx={{
        gridColumn: '1 / -1',
        gridRow: '1 / -1',
        opacity: isActive ? '1' : '0',
        textAlign: 'center',
        transition: 'opacity 0.6s ease-in-out',
      }}
    >
      <Flex mb={[3, null, 4]} sx={{ justifyContent: 'center' }}>
        {[...Array(starCount)].map((x, i) => (
          <Box key={`featured-review-star-${i}`} mx="2px">
            <StarIcon
              sx={{
                color: 'brandTertiary',
                height: '30px',
                width: '30px',
              }}
            />
          </Box>
        ))}
      </Flex>
      {title && (
        <Heading as="h3" variant="subtitle1" mb={2}>
          {title}
        </Heading>
      )}
      {body && (
        <Box
          mb={3}
          sx={{ variant: 'text.quote1', p: { m: 0 } }}
          dangerouslySetInnerHTML={{
            __html: body.childMarkdownRemark.html,
          }}
        />
      )}
      {author && (
        <Text as="p" variant="callout1" m={0}>
          {author}
        </Text>
      )}
      {authorDescription && (
        <Text as="p" color="greyDark" variant="description" m={0}>
          {authorDescription}
        </Text>
      )}
    </Box>
  )
}

const ReviewListSectionFeaturedReviewPager = ({ isActive, onClick, value }) => {
  return (
    <Box onClick={() => onClick()} sx={{ cursor: 'pointer' }}>
      <Button
        variant="reset"
        sx={{
          color: 'transparent',
          display: 'inline-block',
          height: '48px',
          overflow: 'hidden',
          position: 'relative',
          width: '48px',
          '&:before': {
            backgroundColor: isActive ? 'brandPrimary' : 'greyLight',
            borderRadius: '50%',
            content: '""',
            display: 'block',
            height: '12px',
            left: '18px',
            position: 'absolute',
            top: '18px',
            transition: 'background-color 0.6s ease-in-out',
            width: '12px',
          },
        }}
      >
        {value}
      </Button>
    </Box>
  )
}

const ReviewListSectionFeaturedReviews = ({ reviews }) => {
  const [activeItem, setActiveItem] = useState(0)

  useEffect(() => {
    const next = (activeItem + 1) % reviews.length
    const id = setTimeout(() => setActiveItem(next), 5000)
    return () => clearTimeout(id)
  }, [activeItem, reviews])

  return (
    <Box bg="white" pt={[5, 6]} pb={[2, 3]}>
      <Container>
        <VisuallyHidden>
          <Heading as="h2">{'Featured Reviews'}</Heading>
        </VisuallyHidden>
        <Grid columns={[1, null, 12]} mb={1}>
          <Box sx={{ gridColumn: [null, null, '2 / 12'] }}>
            <Grid columns={1} rows={1}>
              {reviews.map((review, index) => (
                <ReviewListSectionFeaturedReview
                  key={`featured-review-${index}`}
                  isActive={activeItem === index}
                  review={review}
                />
              ))}
            </Grid>
          </Box>
        </Grid>
        <Flex sx={{ justifyContent: 'center' }}>
          {reviews.map((item, index) => (
            <ReviewListSectionFeaturedReviewPager
              key={`featured-review-pager-${index}`}
              isActive={activeItem === index}
              onClick={() => setActiveItem(index)}
              value={index}
            />
          ))}
        </Flex>
      </Container>
    </Box>
  )
}

export default ReviewListSectionFeaturedReviews
